import { useEffect, useState, useRef } from "react";
import {
  Button,
  Col,
  Modal,
  Row,
  Table,
  Space,
  message,
  Form,
  Input,
  Select,
  DatePicker,
  Checkbox,
  Radio,
} from "antd";
import * as AntIcons from "@ant-design/icons";
import dayjs from "dayjs";
import { API } from "../../Axios/Axios";
import { getCascadeFieldOptions } from "../../Helpers/APIEndpoints/Endpoints";
import { useActionHandler } from "../ActionHandlerContext";

const EditableFieldSetComponent = ({
  fields = [],
  dataSource = [],
  fieldSetKey = "",
  form,
  InterfaceId,
  handleFieldSetButtonClick = (
    action: string,
    fieldSetKey: string,
    formValue: any,
    targetIndex: any,
  ) => {},
}) => {
  const [gridColumns, setGridColumns] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const { interfaceFieldActionHandler } = useActionHandler();
  const [rowValidationError, setRowValidationError] = useState<string | null>(
    null,
  );
  console.log("fields", fields);
  // cache execParamsList → options
  const optionsCache = useRef<Map<string, any[]>>(new Map());

  const fetchDropdownData = async (fieldId: string, execParams: any) => {
    const cacheKey = fieldId + "_" + JSON.stringify(execParams ?? {});
    if (optionsCache.current.has(cacheKey)) {
      return optionsCache.current.get(cacheKey) || [];
    }

    try {
      const response = await API.post(getCascadeFieldOptions, {
        FieldId: fieldId,
        InterfaceId,
        FormData: JSON.stringify(execParams),
      });

      if (!response || response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response?.status}`);
      }

      const result = response.data?.data ?? [];
      optionsCache.current.set(cacheKey, result); // ✅ cache result
      return result;
    } catch (error) {
      console.error(`Error fetching data for ${fieldId}:`, error);
      return [];
    }
  };

  const handleClick = async (name: string, rowId?: string | number) => {
    const currentField = fields.find((f: { name: string }) => f.name === name);
    if (!currentField?.lazyload) {
      return;
    }

    const data = form.getFieldsValue();

    const execParamsList = (currentField.execParams ?? []).reduce(
      (acc: Record<string, any>, param: { name: string }) => {
        let paramValue: any;

        if (rowId) {
          paramValue = data?.[fieldSetKey]?.[rowId]?.[param.name];
        } else {
          paramValue = data[param.name];
        }

        acc[param.name] = paramValue ?? null;
        return acc;
      },
      {},
    );

    Object.keys(execParamsList).forEach((key) => {
      if (Array.isArray(execParamsList[key])) {
        execParamsList[key] = execParamsList[key].join(",");
      } else if (execParamsList[key]?.$d) {
        execParamsList[key] = dayjs(execParamsList[key]).format(
          "YYYY-MM-DD HH:mm:ss",
        );
      }
    });

    try {
      const options = await fetchDropdownData(currentField.Id, execParamsList);

      setTableData((prev) =>
        prev.map((row) =>
          row.Id === rowId
            ? {
                ...row,
                [`${currentField.name}_options`]:
                  options.length > 0
                    ? options
                    : [{ label: "No Data", value: "" }],
              }
            : row,
        ),
      );
    } catch (error) {
      console.error(`Failed to load data for ${name}:`, error);
    }
  };

  const handleinputOnChange = async (
    sf: any,
    recordId: any,
    fieldSetKey: string,
  ) => {
    if (sf?.onChange) {
      await interfaceFieldActionHandler[sf.onChange]?.(
        form,
        recordId,
        fieldSetKey,
        sf.name,
      );
    }

    const formValue = form.getFieldValue([fieldSetKey, String(recordId)]);

    handleFieldSetButtonClick("Update", fieldSetKey, formValue, recordId);
  };

  const handleSimpleChange = (fieldSetKey: string, recordId: string) => {
    const formValue = form.getFieldValue([fieldSetKey, recordId]);
    handleFieldSetButtonClick("Update", fieldSetKey, formValue, recordId);
  };

  const handleRowUpdate = (fieldSetKey: string, recordId: string) => {
    const formValue = form.getFieldValue([fieldSetKey, recordId]);
    handleFieldSetButtonClick("Update", fieldSetKey, formValue, recordId);
  };

  const handleSimpleFieldChange = (fieldSetKey: string, recordId: string) => {
    const formValue = form.getFieldValue([fieldSetKey, recordId]);
    handleFieldSetButtonClick("Update", fieldSetKey, formValue, recordId);
  };

  const mapOptions = (options: any[] = []) =>
    options.map((opt) => ({
      label: opt.label,
      value: opt.value,
    }));

  const getCheckboxValueProps = (value: any) => ({
    checked: value === true || value === "true" || value === 1 || value === "1",
  });

  const extractCheckedValue = (e: any) => e.target.checked;

  const handlecheckboxChange = (recordId: any) => {
    const formValue = form.getFieldValue([fieldSetKey, String(recordId)]);
    handleFieldSetButtonClick("Update", fieldSetKey, formValue, recordId);
  };
  const getSelectOptions = (options: any[] = []) =>
    options.map((opt) => ({
      label: opt.label,
      value: String(opt.value),
    }));

  const handleMultiSelectChange = (
    recordId: any,
    fieldName: string,
    val: any[],
  ) => {
    form.setFieldValue([fieldSetKey, String(recordId), fieldName], val);

    const formValue = form.getFieldValue([fieldSetKey, String(recordId)]);

    handleFieldSetButtonClick("Update", fieldSetKey, formValue, recordId);
  };

  const handleSelectdropdownChange = async (recordId: any, sf: any) => {
    if (sf?.onChange) {
      await interfaceFieldActionHandler[sf.onChange](
        form,
        recordId,
        fieldSetKey,
      );
    }

    const formValue = form.getFieldValue([fieldSetKey, String(recordId)]);

    handleFieldSetButtonClick("Update", fieldSetKey, formValue, recordId);
  };

  const handleLazyLoadFocus = (fieldName: string, recordId: any) => {
    handleClick(fieldName, recordId);
  };
  const handleFieldSetChange = async (val, sf, form, record, fieldSetKey) => {
    // Keep array directly
    form.setFieldValue([fieldSetKey, String(record.Id), sf.name], val);

    // Execute custom onChange action
    if (sf?.onChange) {
      await interfaceFieldActionHandler[sf.onChange](
        form,
        record.Id,
        fieldSetKey,
      );
    }

    const formValue = form.getFieldValue([fieldSetKey, String(record.Id)]);

    handleFieldSetButtonClick("Update", fieldSetKey, formValue, record.Id);
  };

  const normalizeoptions = (o) =>
    o.map((x) => ({ value: String(x.value), label: x.label }));

  const doesOptionMatch = (option: any, value: string): boolean =>
    String(option.value) === String(value);

  const isValidValue = (value: string, options: any[]): boolean =>
    options.some((opt) => doesOptionMatch(opt, value));

  useEffect(() => {
    const columns: any[] = fields.map((sf) => ({
      key: sf.name,
      dataIndex: sf.name,
      title: sf.label,
      render: (_: any, record: any) => {
        const namePath = [fieldSetKey, String(record.Id), sf.name];

        switch (sf.type?.toLowerCase()) {
          case "input":
            return (
              <Form.Item name={namePath} rules={sf.rules}>
                <Input
                  placeholder={sf.label}
                  disabled={sf?.isFieldDisable}
                  onChange={handleinputOnChange.bind(
                    null,
                    sf,
                    record.Id,
                    fieldSetKey,
                  )}
                />
              </Form.Item>
            );

          case "textarea":
            return (
              <Form.Item name={namePath} rules={sf.rules}>
                <Input.TextArea
                  placeholder={sf.label}
                  rows={3}
                  onChange={handleSimpleChange.bind(
                    null,
                    fieldSetKey,
                    String(record.Id),
                  )}
                />
              </Form.Item>
            );

          case "number":
            return (
              <Form.Item name={namePath} rules={sf.rules}>
                <Input
                  type="number"
                  disabled={sf?.isFieldDisable}
                  placeholder={sf.label}
                  onChange={handleRowUpdate.bind(
                    null,
                    fieldSetKey,
                    String(record.Id),
                  )}
                />
              </Form.Item>
            );

          case "radio":
            return (
              <Form.Item name={namePath} rules={sf.rules}>
                <Radio.Group
                  options={mapOptions(sf.fieldOptions)}
                  onChange={handleSimpleFieldChange.bind(
                    null,
                    fieldSetKey,
                    String(record.Id),
                  )}
                />
              </Form.Item>
            );

          case "checkbox":
            return (
              <Form.Item
                name={namePath}
                valuePropName="checked"
                getValueProps={getCheckboxValueProps}
                getValueFromEvent={extractCheckedValue}
              >
                <Checkbox onChange={handlecheckboxChange.bind(null, record.Id)}>
                  {sf.label}
                </Checkbox>
              </Form.Item>
            );

          case "checkboxmulti":
            return (
              <Form.Item name={namePath} rules={sf.rules}>
                <Checkbox.Group
                  options={getSelectOptions(sf.fieldOptions)}
                  onChange={handleMultiSelectChange.bind(
                    null,
                    record.Id,
                    sf.name,
                  )}
                  value={form.getFieldValue(namePath) ?? []}
                />
              </Form.Item>
            );

          case "select":
            return (
              <Form.Item name={namePath} rules={sf.rules}>
                <Select
                  style={{ width: "100%" }}
                  placeholder={sf.label}
                  disabled={sf?.isFieldDisable}
                  options={normalizeoptions(
                    record[`${sf.name}_options`] ?? sf.fieldOptions ?? [],
                  )}
                  onChange={handleSelectdropdownChange.bind(
                    null,
                    record.Id,
                    sf,
                  )}
                  {...(sf.lazyload
                    ? {
                        onFocus: handleLazyLoadFocus.bind(
                          null,
                          sf.name,
                          record.Id,
                        ),
                      }
                    : {})}
                />
              </Form.Item>
            );

          case "selectmulti":
            return (
              <Form.Item name={namePath} rules={sf?.rules} layout="vertical">
                <Select
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  disabled={sf?.isFieldDisable}
                  mode="multiple"
                  maxTagCount={2}
                  placeholder={sf.label}
                  // options={(
                  //   record[`${sf.name}_options`] ??
                  //   sf.fieldOptions ??
                  //   []
                  // ).map((opt: { value: any; label: any }) => ({
                  //   value: String(opt.value),
                  //   label: opt.label,
                  // }))}
                  options={normalizeoptions(
                    record[`${sf.name}_options`] ?? sf.fieldOptions ?? [],
                  )}
                  onChange={handleFieldSetChange}
                  value={form.getFieldValue(namePath) ?? []}
                  {...(sf.lazyload
                    ? // ? { onFocus: () => void handleClick(sf.name, record.Id) }
                      {
                        onFocus: handleLazyLoadFocus.bind(
                          null,
                          sf.name,
                          record.Id,
                        ),
                      }
                    : {})}
                />
              </Form.Item>
            );

          case "date":
            return (
              <Form.Item name={namePath} rules={sf.rules}>
                <DatePicker
                  style={{ width: "100%" }}
                  disabled={sf?.isFieldDisable}
                  onChange={handleFieldSetChange}
                  // onChange={async () => {
                  //   if (sf?.onChange) {
                  //     await interfaceFieldActionHandler[sf?.onChange](
                  //       form,
                  //       record.Id,
                  //       fieldSetKey
                  //     );
                  //   }
                  //   const formValue = form.getFieldValue([
                  //     fieldSetKey,
                  //     String(record.Id),
                  //   ]);
                  //   handleFieldSetButtonClick(
                  //     "Update",
                  //     fieldSetKey,
                  //     formValue,
                  //     record.Id
                  //   );
                  // }}
                />
              </Form.Item>
            );

          default:
            return null;
        }
      },
    }));

    columns.push({
      key: "gridaction",
      title: "Action",
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="primary"
            danger
            icon={<AntIcons.DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    });

    setGridColumns(columns);
  }, [fields, form, interfaceFieldActionHandler]);

  // preload data
  useEffect(() => {
    if (!dataSource) {
      setTableData([]);
      return;
    }

    const normalizeRows = async () => {
      // NOSONAR : igonre
      const normalizedRows: any[] = [];

      for (const row of dataSource) {
        const newRow: any = { ...row };

        for (const f of fields) {
          const type = f.type?.toLowerCase();

          if (type === "selectmulti" && newRow[f.name]) {
            if (typeof newRow[f.name] === "string") {
              newRow[f.name] = newRow[f.name]
                .split(",")
                .map((s: string) => s.trim())
                .filter(Boolean);
            }

            if (f.lazyload && newRow[f.name]?.length > 0) {
              try {
                const execParamsList = (f.execParams ?? []).reduce(
                  (acc: Record<string, any>, param) => {
                    acc[param.name] = row[param.name] ?? null;
                    return acc;
                  },
                  {},
                );

                const options = await fetchDropdownData(f.Id, execParamsList);
                newRow[`${f.name}_options`] = options;

                const validValues = newRow[f.name].filter((val: string) =>
                  // options.some((opt) => String(opt.value) === String(val))
                  isValidValue(val, options),
                );
                form.setFieldValue(
                  [fieldSetKey, String(row.Id), f.name],
                  validValues,
                );
                newRow[f.name] = validValues;
              } catch (err) {
                console.error(`Lazyload preload failed for ${f.name}`, err);
              }
            }
          }

          if (type === "checkboxmulti" && newRow[f.name]) {
            if (typeof newRow[f.name] === "string") {
              newRow[f.name] = newRow[f.name]
                .split(",")
                .map((s: string) => s.trim())
                .filter(Boolean);
            }
          }

          if (type === "select" && newRow[f.name] != null) {
            newRow[f.name] = String(newRow[f.name]);

            if (f.lazyload && newRow[f.name]) {
              try {
                const execParamsList = (f.execParams ?? []).reduce(
                  (acc: Record<string, any>, param) => {
                    acc[param.name] = row[param.name] ?? null;
                    return acc;
                  },
                  {},
                );

                const options = await fetchDropdownData(f.Id, execParamsList);
                newRow[`${f.name}_options`] = options;

                const exists = options.some(
                  (opt) => String(opt.value) === String(newRow[f.name]),
                );
                form.setFieldValue(
                  [fieldSetKey, String(row.Id), f.name],
                  exists ? newRow[f.name] : null,
                );

                if (!exists) newRow[f.name] = null;
              } catch (err) {
                console.error(`Lazyload preload failed for ${f.name}`, err);
              }
            }
          }

          if (type === "date" && newRow[f.name]) {
            newRow[f.name] = dayjs(newRow[f.name]);
          }
        }

        normalizedRows.push(newRow);
      }

      setTableData(normalizedRows);

      for (const row of normalizedRows) {
        for (const f of fields) {
          const currentValue = form.getFieldValue([
            fieldSetKey,
            String(row.Id),
            f.name,
          ]);
          if (currentValue == null || currentValue === "") {
            form.setFieldValue(
              [fieldSetKey, String(row.Id), f.name],
              row[f.name] ?? null,
            );
          }
        }
      }
    };
    if (dataSource.length > 0) {
      normalizeRows();
      setRowValidationError(null);
    } else {
      setRowValidationError("At least one row is required.");
    }
  }, [dataSource, fields, fieldSetKey]);

  const handleAdd = () => {
    const maxId =
      tableData.length > 0
        ? Math.max(...tableData.map((row) => Number(row.Id) || 0))
        : 0;

    const newRow: any = { Id: maxId + 1 };
    fields.forEach((f) => {
      newRow[f.name] =
        f.defaultValue === "" || f.defaultValue == null ? null : f.defaultValue;
    });

    setTableData((prev) => {
      const updated = [...prev, newRow];
      // validate after adding
      if (updated.length >= 1) setRowValidationError(null);
      return updated;
    });

    fields.forEach((f) => {
      form.setFieldValue(
        [fieldSetKey, String(newRow.Id), f.name],
        newRow[f.name],
      );
    });

    handleFieldSetButtonClick("Add", fieldSetKey, newRow, newRow.Id);
  };

  const handleDelete = (record: any) => {
    Modal.confirm({
      title: "Are you sure you want to delete this record?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: () => {
        const updatedData = tableData.filter((item) => item.Id !== record.Id);
        setTableData(updatedData);

        const rows = { ...(form.getFieldValue([fieldSetKey]) ?? {}) };
        delete rows[String(record.Id)];

        form.setFieldValue([fieldSetKey], rows);
        // validate after delete
        if (updatedData.length < 1) {
          setRowValidationError("At least one row is required.");
        } else {
          setRowValidationError(null);
        }

        handleFieldSetButtonClick("Delete", fieldSetKey, null, record.Id);
        message.success("Record deleted successfully!");
      },
    });
  };

  console.log("eeror", rowValidationError);

  return (
    <>
      <Row justify="end" style={{ marginBottom: 16 }}>
        <Col>
          <Button
            type="primary"
            icon={<AntIcons.PlusOutlined />}
            onClick={handleAdd}
          >
            Add New
          </Button>
        </Col>
      </Row>

      <Table
        bordered
        rowKey="Id"
        dataSource={tableData}
        columns={gridColumns}
        pagination={false} // if pagination not required
        // pagination={{
        //   showTotal: (totalcount, range) =>
        //     `Showing ${range[0]} to ${range[1]} of ${totalcount} entries`,
        //   pageSize: 5, // show 5 rows per page (you can change to 10, 20, etc.)
        // }} // if pagination required
      />
      {rowValidationError && (
        <div style={{ color: "red", marginTop: 8 }}>{rowValidationError}</div>
      )}
      <br />
    </>
  );
};

export default EditableFieldSetComponent;
